import { combineReducers } from 'redux';
import { connectRouter } from 'connected-react-router';
import { modelReducer, formReducer } from 'react-redux-form';
import update from 'react-addons-update';
import pick from 'lodash/pick';
import attachmentUploadReducer from './attachment-upload';
import configurationReducer from './configuration-reducers';
import iframeModalReducer from './iframe-modal-reducer';
import issuesReducer from './issues-reducers';
import issuePreviewReducer from './issue-preview-reducer';
import thumbnailsReducer from './thumbnails-reducers';
import workflowsReducer from './workflows-reducer';
import attachmentsReducer from './attachments-reducer';
import galleryReducer from './gallery-reducer';
import currentUserReducer from './current-user-reducer';
import permissionsReducer from './permissions-reducer';
import deepLinkingReducer from './deepLinking-reducer';
import notificationManagerReducer from './notificationManagerReducer';
import rfiCustomIdentifierReducer from './rfi-custom-identifier-reducer';
import { ISSUE_ATTRIBUTE_NAMES } from '../../constants/issue-attribute-names';
import { STATUS_DRAFT } from '../../constants/status-types';
import {
  SET_FF_LOADED,
  ACCESS_TOKEN_SET,
  TARGET_URN_SET,
  PREVIEW_ISSUE_ID_SET,
  ISSUE_INSTANCE_FETCH_API_REJECTED,
  SHOW_NEW_ISSUE_MODAL,
  PUSHPIN_WORKFLOW_ENABLED,
  PUSHPIN_ATTRIBUTES_SET,
  QUERY_PARAMS_SET,
  SET_QUERY_PARAMS_FROM_URL,
  SET_CURRENT_VIEW,
  ISSUES_FETCH_REPORT_START,
  ISSUES_FETCH_REPORT_API_REJECTED,
  ISSUES_FETCH_REPORT_API_FULFILLED,
  ISSUES_CREATE_PDF_REPORT_START,
  ISSUES_CREATE_PDF_REPORT_API_REJECTED,
  ISSUES_CREATE_PDF_REPORT_API_FULFILLED,
  SET_CURRENT_PROJECT,
  CURRENT_DOCS_PROJECT_FETCH_START,
  CURRENT_DOCS_PROJECT_FETCH_FINISHED,
  CURRENT_DOCS_PROJECT_FETCH_FAILED,
  SET_PUSHPIN_VISIBILITY_POPUP,
  TOGGLE_PUSHPIN_VISIBILITY,
  TOGGLE_PUSHPIN_VISIBILITY_SUCCESS,
  TOGGLE_PUSHPIN_VISIBILITY_FAIL,
  RFIS_CSV_REPORT_DOWNLOADED,
  SET_DELETE_PUSHPIN_POPUP,
  DELETE_PUSHPIN,
  DELETE_PUSHPIN_SUCCESS,
  DELETE_PUSHPIN_FAIL,
} from '../actions';

import { getPreloadedQueryParamsState } from '../../constants/preloaded-states';
import { hasIssueUIParamsChanged, updateUrlWithQueryParams } from '../../utils/query-helpers';
import featureFlags, { FEATURE_FLAGS } from '../../utils/featureFlags';

/**
 * Do not dispatch from within reducers.
 *
 * All of the following should be pure, side-effect free functions:
 */

const preloadedPendingIssueState = {
  [ISSUE_ATTRIBUTE_NAMES.STATUS]: STATUS_DRAFT,
  [ISSUE_ATTRIBUTE_NAMES.TITLE]: '',
  [ISSUE_ATTRIBUTE_NAMES.DESCRIPTION]: '',
  [ISSUE_ATTRIBUTE_NAMES.LOCATION_DESCRIPTION]: '',
  [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: '',
  [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]: '',
  [ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER]: '',
};

/* eslint-disable default-case */

const ffLoadedReducer = (state = false, action) => {
  switch (action.type) {
    case SET_FF_LOADED:
      return true;
    default:
      return state;
  }
};

const accessTokenReducer = (state = null, action) => {
  switch (action.type) {
    case ACCESS_TOKEN_SET:
      return action.payload;
  }
  return state;
};

const targetUrnReducer = (state = null, action) => {
  switch (action.type) {
    case TARGET_URN_SET:
      return action.payload;
  }
  return state;
};

const pushpinWorkflowReducer = (state = true, action) => {
  switch (action.type) {
    case PUSHPIN_WORKFLOW_ENABLED:
      return action.payload;
  }
  return state;
};

const currentProjectReducer = (state = {}, action) => {
  switch (action.type) {
    case SET_CURRENT_PROJECT:
      return update(state, { currentProject: { $set: action.payload }});
    case CURRENT_DOCS_PROJECT_FETCH_START:
      return update(state, { loading: { $set: true }});
    case CURRENT_DOCS_PROJECT_FETCH_FINISHED:
      return update(state, { loading: { $set: false }, docsCurrentProject: { $set: action.payload }});
    case CURRENT_DOCS_PROJECT_FETCH_FAILED:
      return update(state, { loading: { $set: false }, error: { $set: action.payload }});
  }
  return state;
};

const issuePushpinReducer = (state = null, action) => {
  switch (action.type) {
    case PUSHPIN_ATTRIBUTES_SET: {
      return action.payload;
    }
  }
  return state;
};

const queryParamsReducer = (state = null, action) => {
  switch (action.type) {
    case QUERY_PARAMS_SET: {
      const queryParams = action.payload;
      const urlUpdateResult = updateUrlWithQueryParams(queryParams);
      let newState = update(state, { $merge: queryParams });
      if (urlUpdateResult.hasBeenUpdated) {
        newState = update(newState, { currentLocation: { $set: pick(urlUpdateResult, ['pathname', 'queryParamsString']) }});
      }

      return newState;
    }

    case SET_QUERY_PARAMS_FROM_URL: {
      if (hasIssueUIParamsChanged(state.currentLocation, action.payload.newLocation)) {
        const queryParams = action.payload.queryParams;
        const currentView = action.payload.currentView;
        const updatedQueryParams = Object.assign({}, getPreloadedQueryParamsState()[currentView], queryParams);
        let newState = update(state, { $merge: updatedQueryParams });
        newState = update(newState, { hasSetQueryParamsFromUrl: { $set: true }});
        return update(newState, { currentLocation: { $set: action.payload.newLocation }});
      } else {
        return state;
      }
    }

    case SET_CURRENT_VIEW: {
      const view = action.payload;
      return getPreloadedQueryParamsState()[view] || state;
    }
  }
  return state;
};

const currentPreviewIssueIdReducer = (state = null, action) => {
  switch (action.type) {
    case PREVIEW_ISSUE_ID_SET:
      return action.payload;
    case ISSUE_INSTANCE_FETCH_API_REJECTED:
      return null;
  }
  return state;
};

const showNewIssueModalReducer = (state = false, action) => {
  switch (action.type) {
    case SHOW_NEW_ISSUE_MODAL:
      return action.payload;
  }
  return state;
};

const currentViewTypeReducer = (state = null, action) => {
  switch (action.type) {
    case SET_CURRENT_VIEW:
      return action.payload;
  }
  return state;
};

const reportReducer = (state = {}, action) => {
  switch (action.type) {
    case ISSUES_FETCH_REPORT_START:
      return update(state, { isFetchReportLoading: { $set: true }});
    case ISSUES_FETCH_REPORT_API_REJECTED:
    case ISSUES_FETCH_REPORT_API_FULFILLED:
    case RFIS_CSV_REPORT_DOWNLOADED:
      return update(state, { isFetchReportLoading: { $set: false }});
    case ISSUES_CREATE_PDF_REPORT_START:
      return update(state, { isCreatingPdfReport: { $set: true }});
    case ISSUES_CREATE_PDF_REPORT_API_REJECTED:
    case ISSUES_CREATE_PDF_REPORT_API_FULFILLED:
      return update(state, { isCreatingPdfReport: { $set: false }});
  }
  return state;
};

const initialPushpinVisibilityPopUpReducerState = { visible: false, loading: false };
const pushpinVisibilityPopUpReducer = (state = initialPushpinVisibilityPopUpReducerState, action) => {
  switch (action.type) {
    case SET_PUSHPIN_VISIBILITY_POPUP:
      return update(state, { visible: { $set: action.payload }});
    case TOGGLE_PUSHPIN_VISIBILITY:
      return update(state, { loading: { $set: true }});
    case TOGGLE_PUSHPIN_VISIBILITY_SUCCESS:
    case TOGGLE_PUSHPIN_VISIBILITY_FAIL:
      return initialPushpinVisibilityPopUpReducerState;
  }
  return state;
};

const initialDeletePushpinPopUpReducerState = { visible: false };
const deletePushpinPopUpReducer = (state = initialDeletePushpinPopUpReducerState, action) => {
  switch (action.type) {
    case SET_DELETE_PUSHPIN_POPUP:
      return update(state, { visible: { $set: action.payload }});
    case DELETE_PUSHPIN:
      return update(state, { loading: { $set: true }});
    case DELETE_PUSHPIN_SUCCESS:
    case DELETE_PUSHPIN_FAIL:
      return initialDeletePushpinPopUpReducerState;
  }
  return state;
};

export default (history) => combineReducers({
  configuration: configurationReducer,
  ffLoaded: ffLoadedReducer,
  accessToken: accessTokenReducer,
  targetUrn: targetUrnReducer,
  report: reportReducer,
  router: connectRouter(history),
  currentUser: currentUserReducer,
  currentProject: currentProjectReducer,
  iframeModal: iframeModalReducer,
  issuePushpin: issuePushpinReducer,
  issues: issuesReducer,
  issuePreview: issuePreviewReducer,
  workflows: workflowsReducer,
  attachments: attachmentsReducer,
  gallery: galleryReducer,
  pendingIssue: modelReducer('pendingIssue', preloadedPendingIssueState),
  pendingIssueForm: formReducer('pendingIssue'),
  transitionForm: formReducer('transitionForm'),
  queryParams: queryParamsReducer,
  attachmentUpload: attachmentUploadReducer,
  thumbnails: thumbnailsReducer,
  pushpinWorkflowEnabled: pushpinWorkflowReducer,
  currentPreviewIssueId: currentPreviewIssueIdReducer,
  showNewIssueModal: showNewIssueModalReducer,
  currentView: currentViewTypeReducer,
  pushpinVisibilityPopUp: pushpinVisibilityPopUpReducer,
  deletePushpinPopUp: deletePushpinPopUpReducer,
  permissions: permissionsReducer,
  deepLinking: deepLinkingReducer,
  notification: notificationManagerReducer,
  rfiCustomIdentifier: rfiCustomIdentifierReducer,
});
